/**
 * PLUGIN LOADER — Discovery automatica via import.meta.glob.
 * Il core NON importa MAI i plugin direttamente.
 * Pattern: plugins/plugin-[name]/src/index.ts (relativo a packages/)
 */

import type { PluginManifest } from '@crm/contracts';
import { registerPlugin } from './registry';

type PluginModule = { default: PluginManifest };

/**
 * I plugin DEMO si caricano solo con entrambe le variabili accese.
 *
 * Non è una precauzione doppia. `VITE_ENABLE_DEMO_PLUGIN` dice se li vuoi;
 * `VITE_USE_MOCKS` è una condizione di funzionamento, perché i dati di un plugin
 * demo vivono solo negli handler MSW: senza mock sarebbe presente e rotto.
 *
 * Vite sostituisce entrambe con letterali a build time. Nei tre profili Jenkins
 * valgono 'false', quindi questa costante diventa staticamente falsa, il ramo che
 * la usa è irraggiungibile e la `import.meta.glob` che contiene sparisce dal
 * bundle insieme ai chunk che genererebbe. È così che un plugin demo non arriva
 * in produzione — non nascosto, proprio assente.
 */
const demoEnabled =
  import.meta.env.VITE_ENABLE_DEMO_PLUGIN === 'true' &&
  import.meta.env.VITE_USE_MOCKS === 'true';

/**
 * I plugin di PRODOTTO. Il pattern negativo tiene fuori i demo: se li prendesse
 * questa glob finirebbero nel bundle qualunque cosa dica il flag, perché una glob
 * incondizionata è sempre risolta a build time.
 *
 * È l'unica nozione che il core ha dei plugin demo: un pattern di nome, non una
 * dipendenza. Non c'è modo di evitarla — perché il codice non finisca nel build,
 * la glob non deve nominarlo, e per non nominarlo deve saperlo distinguere.
 */
const pluginModules = import.meta.glob<PluginModule>([
  '../../../plugins/plugin-*/src/index.ts',
  '!../../../plugins/plugin-demo*/src/index.ts',
]);

const demoModules = demoEnabled
  ? import.meta.glob<PluginModule>('../../../plugins/plugin-demo*/src/index.ts')
  : {};

export async function loadPlugins(): Promise<void> {
  // La provenienza viaggia con l'entry: serve a valle, quando si registra, e
  // ricavarla dal path significherebbe ripetere il pattern in un secondo posto.
  const entries = [
    ...Object.entries(pluginModules).map(([path, loader]) => ({
      path,
      loader,
      isDemo: false,
    })),
    ...Object.entries(demoModules).map(([path, loader]) => ({
      path,
      loader,
      isDemo: true,
    })),
  ];

  if (entries.length === 0) {
    console.info('[Plugin Loader] Nessun plugin trovato.');
    return;
  }

  const results = await Promise.all(
    entries.map(async ({ path, loader, isDemo }) => {
      try {
        const module = await loader();
        return { path, manifest: module.default, isDemo, error: null };
      } catch (err) {
        console.error(`[Plugin Loader] Errore: ${path}`, err);
        return { path, manifest: null, isDemo, error: err };
      }
    })
  );

  for (const { manifest, isDemo } of results) {
    if (manifest) {
      registerPlugin(manifest, isDemo);
      // console.info(`[Plugin Loader] Registrato: ${manifest.id} (${manifest.name}) da ${path}`);
      console.info('[Plugin Loader] Plugin registrato.');
    }
  }
}
